import React, { useState, useTransition, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { MorphingButton, CursorAttractor, RippleContainer } from '../styles/microInteractions';

export interface UserData {
  name: string;
  email: string;
  phone: string;
  isNewUser: boolean;
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (userData: UserData) => void;
}

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(12px);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

// Styled Components
const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(12px);
  z-index: ${DesignSystem.zIndex.modal + 10};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${getSpacing('lg')};
  animation: ${fadeIn} ${DesignSystem.motion.duration.normal} ease-out;
  opacity: ${props => props.$isOpen ? 1 : 0};
  pointer-events: ${props => props.$isOpen ? 'all' : 'none'};
  transition: opacity ${DesignSystem.motion.duration.normal} ease;
`;

const ModalContainer = styled.div<{ $isOpen: boolean }>`
  background: linear-gradient(135deg, 
    ${getColor('primary.50')} 0%,
    ${getColor('accent.50')} 100%
  );
  border-radius: ${DesignSystem.borderRadius.xl};
  box-shadow: ${getShadow('luxe')}, 
              0 0 60px rgba(165, 157, 132, 0.3);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideUp} ${DesignSystem.motion.duration.slow} cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid ${getColor('accent.200')};
  
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${getSpacing('lg')};
  right: ${getSpacing('lg')};
  background: ${getColor('primary.100')};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 1.5rem;
  color: ${getColor('primary.800')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  z-index: 10;

  &:hover {
    background: ${getColor('secondary.500')};
    color: white;
    transform: rotate(90deg) scale(1.1);
    box-shadow: ${getShadow('large')};
  }
`;

const Content = styled.div`
  padding: ${getSpacing('2xl')};
  
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    padding: ${getSpacing('xl')};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${getSpacing('xl')};
`;

const IconWrapper = styled.div<{ $isSignUp: boolean }>`
  width: 80px;
  height: 80px;
  margin: 0 auto ${getSpacing('lg')};
  background: linear-gradient(135deg, 
    ${props => props.$isSignUp ? getColor('secondary.400') : getColor('primary.600')} 0%,
    ${props => props.$isSignUp ? getColor('secondary.600') : getColor('primary.800')} 100%
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${getShadow('large')};
  font-size: 2.5rem;
  transition: all ${DesignSystem.motion.duration.normal} ease;
  
  &:hover {
    transform: scale(1.05) rotate(5deg);
    box-shadow: ${getShadow('luxe')};
  }
`;

const Title = styled.h2`
  font-size: ${DesignSystem.typography.scale.h3.size};
  font-weight: 700;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('sm')} 0;
  line-height: ${DesignSystem.typography.scale.h3.lineHeight};
`;

const Subtitle = styled.p`
  font-size: ${DesignSystem.typography.scale.body.size};
  color: ${getColor('primary.700')};
  margin: 0;
  line-height: 1.6;
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${getSpacing('sm')};
  margin-bottom: ${getSpacing('xl')};
  background: ${getColor('primary.100')};
  padding: ${getSpacing('xs')};
  border-radius: ${DesignSystem.borderRadius.lg};
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  border: none;
  border-radius: ${DesignSystem.borderRadius.md};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  position: relative;
  overflow: hidden;
  
  ${props => props.$active ? css`
    background: linear-gradient(135deg, 
      ${getColor('secondary.500')} 0%,
      ${getColor('secondary.600')} 100%
    );
    color: white;
    box-shadow: ${getShadow('subtle')};
    transform: translateY(-2px);
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      animation: ${shimmer} 2s infinite;
    }
  ` : css`
    background: transparent;
    color: ${getColor('primary.700')};
    
    &:hover {
      background: ${getColor('primary.200')};
      transform: translateY(-1px);
    }
  `}
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('lg')};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('sm')};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${getColor('primary.800')};
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: ${getSpacing('xs')};
`;

const RequiredMark = styled.span`
  color: ${getColor('secondary.500')};
  font-size: 1.1rem;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  border: 2px solid ${props => props.$hasError ? getColor('secondary.500') : getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.md};
  font-size: 1rem;
  color: ${getColor('primary.900')};
  background: white;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? getColor('secondary.600') : getColor('accent.500')};
    box-shadow: 0 0 0 3px ${props => props.$hasError 
      ? 'rgba(217, 119, 87, 0.15)' 
      : 'rgba(233, 226, 195, 0.3)'};
    transform: translateY(-1px);
  }
  
  &::placeholder {
    color: ${getColor('primary.400')};
  }
  
  &:disabled {
    background: ${getColor('primary.50')};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const ErrorMessage = styled.div`
  color: ${getColor('secondary.600')};
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: ${getSpacing('xs')};
  animation: ${pulse} ${DesignSystem.motion.duration.fast} ease-in-out;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('md')};
  margin-top: ${getSpacing('lg')};
`;

const SubmitButton = styled(MorphingButton)<{ $isLoading?: boolean }>`
  width: 100%;
  padding: ${getSpacing('md')} ${getSpacing('xl')};
  font-size: 1.1rem;
  font-weight: 700;
  background: linear-gradient(135deg, 
    ${getColor('primary.700')} 0%,
    ${getColor('primary.900')} 100%
  );
  color: white;
  border: none;
  border-radius: ${DesignSystem.borderRadius.md};
  cursor: ${props => props.$isLoading ? 'wait' : 'pointer'};
  transition: all ${DesignSystem.motion.duration.normal} ease;
  position: relative;
  overflow: hidden;
  
  ${props => props.$isLoading && css`
    opacity: 0.8;
    pointer-events: none;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      animation: ${shimmer} 1.5s infinite;
    }
  `}
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${getShadow('large')};
    background: linear-gradient(135deg, 
      ${getColor('primary.800')} 0%,
      ${getColor('primary.900')} 100%
    );
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, 
    ${getColor('accent.100')} 0%,
    ${getColor('accent.50')} 100%
  );
  border-left: 4px solid ${getColor('secondary.500')};
  padding: ${getSpacing('md')};
  border-radius: ${DesignSystem.borderRadius.md};
  margin-top: ${getSpacing('lg')};
  display: flex;
  gap: ${getSpacing('md')};
  align-items: start;
`;

const InfoIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${getColor('secondary.500')};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: ${getColor('primary.800')};
  line-height: 1.5;
`;

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onAuthSuccess }) => {
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [isPending, startTransition] = useTransition();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidEmail = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return formData.email === '' || emailRegex.test(formData.email);
  }, [formData.email]);

  const isValidPhone = useMemo(() => {
    const phoneRegex = /^[0-9]{10}$/;
    return formData.phone === '' || phoneRegex.test(formData.phone);
  }, [formData.phone]);

  const handleInputChange = (field: string, value: string) => {
    startTransition(() => {
      setFormData(prev => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (authMode === 'signup' && !formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhone) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (authMode === 'signup' && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (authMode === 'signup') {
        // call signup API
        const payload = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          meta: { source: 'frontend-authmodal' }
        };
        // dynamic import to avoid circular issues
        const auth = await import('../api/auth');
        const res = await auth.signUp(payload);
        const userData: UserData = {
          name: res.user?.name || formData.name || 'Guest',
          email: res.user?.email || formData.email,
          phone: res.user?.phone || formData.phone,
          isNewUser: true
        };
        onAuthSuccess(userData);
      } else {
        // signin
        const payload = {
          email: formData.email,
          phone: formData.phone,
          meta: { source: 'frontend-authmodal' }
        };
        const auth = await import('../api/auth');
        const res = await auth.signIn(payload);
        const userData: UserData = {
          name: formData.name || 'Guest',
          email: payload.email,
          phone: payload.phone,
          isNewUser: false
        };
        onAuthSuccess(userData);
      }
    } catch (err: any) {
      console.error('Auth API error:', err);
      setErrors(prev => ({ ...prev, form: err?.message || 'Failed to submit' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTabChange = (mode: 'signin' | 'signup') => {
    startTransition(() => {
      setAuthMode(mode);
      setErrors({});
    });
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <ModalContainer $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <Content>
          <Header>
            {/* Only show the circular IconWrapper for signup; remove the circle for signin */}
            {authMode === 'signup' ? (
              <IconWrapper $isSignUp>
                ✨
              </IconWrapper>
            ) : null}

            <Title>
              {authMode === 'signup' ? 'Create Your Account' : 'Sign In'}
            </Title>

            {authMode === 'signup' && (
              <Subtitle>
                Join us to start your amazing journey to Ladakh
              </Subtitle>
            )}
          </Header>

          <TabContainer>
            <Tab 
              $active={authMode === 'signin'} 
              onClick={() => handleTabChange('signin')}
              type="button"
            >
              Sign In
            </Tab>
            <Tab 
              $active={authMode === 'signup'} 
              onClick={() => handleTabChange('signup')}
              type="button"
            >
              Sign Up
            </Tab>
          </TabContainer>

          <Form onSubmit={handleSubmit}>
            {authMode === 'signup' && (
              <FormGroup>
                <Label>
                  Full Name <RequiredMark>*</RequiredMark>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  $hasError={!!errors.name}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <ErrorMessage>
                    ⚠️ {errors.name}
                  </ErrorMessage>
                )}
              </FormGroup>
            )}

            <FormGroup>
              <Label>
                Email Address <RequiredMark>*</RequiredMark>
              </Label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                $hasError={!!errors.email}
                disabled={isSubmitting}
              />
              {errors.email && (
                <ErrorMessage>
                  ⚠️ {errors.email}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>
                Phone Number <RequiredMark>*</RequiredMark>
              </Label>
              <Input
                type="tel"
                placeholder="10-digit mobile number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                $hasError={!!errors.phone}
                disabled={isSubmitting}
              />
              {errors.phone && (
                <ErrorMessage>
                  ⚠️ {errors.phone}
                </ErrorMessage>
              )}
            </FormGroup>

            {authMode === 'signup' && (
              <FormGroup>
                <Label>
                  Password <RequiredMark>*</RequiredMark>
                </Label>
                <Input
                  type="password"
                  placeholder="Create a secure password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  $hasError={!!errors.password}
                  disabled={isSubmitting}
                />
                {errors.password && (
                  <ErrorMessage>
                    ⚠️ {errors.password}
                  </ErrorMessage>
                )}
              </FormGroup>
            )}

            <ButtonGroup>
              <CursorAttractor>
                <RippleContainer>
                  <SubmitButton 
                    type="submit"
                    $isLoading={isSubmitting}
                  >
                    {isSubmitting 
                      ? 'Processing...' 
                      : authMode === 'signup' 
                        ? 'Create Account & Continue' 
                        : 'Sign In & Continue'}
                  </SubmitButton>
                </RippleContainer>
              </CursorAttractor>
            </ButtonGroup>
          </Form>

          <InfoBox>
            <InfoIcon>ℹ</InfoIcon>
            <InfoText>
              {authMode === 'signup' 
                ? 'By signing up, you agree to receive booking confirmations and travel updates via email and SMS.'
                : 'Your session is secure. We\'ll use your details to process your booking and send travel updates.'}
            </InfoText>
          </InfoBox>
        </Content>
      </ModalContainer>
    </Overlay>
  );
};

export default AuthModal;
